import { PageHeader, ErrorState } from '../../components/primitives.jsx';
import { api } from '../../lib/apiClient.js';
import { useApi, useMutation } from '../../lib/useApi.js';
import './admin.css';

function modeLabel(mode) {
  return mode === 'cac_only' ? 'CAC only' : 'Password-first hybrid';
}

function formatOccurredAt(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown time' : date.toLocaleString();
}

export function AuthenticationAdminPage() {
  const authConfig = useApi('/admin/authentication');
  const auditEvents = useApi('/audit', {
    query: {
      action: 'auth.mode.change',
      limit: 8,
    },
  });
  const saveMode = useMutation();

  if (authConfig.error && !authConfig.data) {
    return <ErrorState error={authConfig.error} />;
  }

  const config = authConfig.data?.data || null;
  const events = auditEvents.data?.data || [];

  async function handleSwitch(mode) {
    const response = await saveMode.run(() => api.put('/admin/authentication', { mode }));
    const next = response?.data || response;
    authConfig.setData((current) => ({
      ...(current || {}),
      data: next,
    }));
  }

  return (
    <>
      <PageHeader
        eyebrow="Administration"
        title="Authentication controls"
        subtitle="Switch the live PRIME sign-in posture, confirm environment locks, and review recent auth-control changes."
      />

      <section className="bento admin-bento auth-admin-layout">
        <div className="span-7 card auth-admin-card">
          <div className="eyebrow">Mode selection</div>
          <h2 className="card-title">Current sign-in mode</h2>
          <div className="auth-admin-mode-grid">
            <article className={`auth-admin-mode ${config?.effectiveMode === 'password_first_hybrid' ? 'selected' : ''}`}>
              <span className="chip blue sm">Hybrid mode</span>
              <p className="muted">
                Approved users sign in with email/password first, while CAC stays available
                as a linked alternate path.
              </p>
              <button
                type="button"
                className="btn primary sm"
                disabled={config?.isLocked || config?.effectiveMode === 'password_first_hybrid' || saveMode.running}
                onClick={() => handleSwitch('password_first_hybrid')}
              >
                Enable hybrid mode
              </button>
            </article>

            <article className={`auth-admin-mode ${config?.effectiveMode === 'cac_only' ? 'selected' : ''}`}>
              <span className="chip amber sm">CAC only</span>
              <p className="muted">
                New password logins stop immediately, while already-issued password sessions are
                allowed to finish naturally.
              </p>
              <button
                type="button"
                className="btn secondary sm"
                disabled={config?.isLocked || config?.effectiveMode === 'cac_only' || saveMode.running}
                onClick={() => handleSwitch('cac_only')}
              >
                Switch to CAC only
              </button>
            </article>
          </div>
        </div>

        <div className="span-5 card auth-admin-card">
          <div className="eyebrow">Environment posture</div>
          <h2 className="card-title">Runtime controls</h2>
          <div className="auth-admin-facts">
            <div className="auth-admin-fact">
              <span>Effective mode</span>
              <strong>{modeLabel(config?.effectiveMode)}</strong>
            </div>
            <div className="auth-admin-fact">
              <span>Environment lock</span>
              <strong>{config?.isLocked ? modeLabel(config?.lockedMode) : 'Not locked'}</strong>
            </div>
            <div className="auth-admin-fact">
              <span>CAC availability</span>
              <strong>{config?.cacAvailable ? 'Available' : 'Unavailable'}</strong>
            </div>
          </div>
        </div>

        <div className="span-12 card auth-admin-card">
          <div className="eyebrow">Audit trail</div>
          <h2 className="card-title">Recent authentication control activity</h2>
          {events.length ? (
            <div className="admin-list">
              {events.map((event) => (
                <article key={event.id} className="admin-list-row">
                  <div className="admin-list-row__body">
                    <strong className="admin-list-row__title">{event.action}</strong>
                    <div className="muted small">
                      {event.actorUserId || 'Unknown actor'} · {formatOccurredAt(event.occurredAt)}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <p className="muted">No recent authentication control changes have been recorded yet.</p>
          )}
        </div>
      </section>
    </>
  );
}
